import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bookmark,
  Calendar,
  Wallet,
  Plane,
  Hotel,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Heart,
  Zap,
  Star,
  Share2,
  Image,
  Home,
  Users,
  User,
  ArrowLeft,
  Navigation,
  Sunrise,
  Sunset,
  Moon,
  Coffee,
  Utensils,
  Camera,
  Music,
  ExternalLink,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlanStore } from '../store/planStore';
import { useAuth } from '../contexts/AuthContext';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import api from '../services/api';
import TimelineGenerator from '../components/TimelineGenerator';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export default function Result() {
  const navigate = useNavigate();
  const { currentPlan, addSavedPlan } = usePlanStore();
  const { user } = useAuth();
  const { currency, formatCurrency: contextFormatCurrency } = useLocationContext();
  
  const [showExplanation, setShowExplanation] = useState(false);
  const [expandedDays, setExpandedDays] = useState([1]);
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [destinationEvents, setDestinationEvents] = useState([]);
  
  // Fetch events at destination
  useEffect(() => {
    const fetchEvents = async () => {
      if (currentPlan?.destination) {
        try {
          const data = await api.searchEvents({ city: currentPlan.destination, limit: 3 });
          if (data.events) {
            setDestinationEvents(data.events.slice(0, 3));
          }
        } catch (error) {
          console.error('Error fetching events:', error);
        }
      }
    };
    fetchEvents();
  }, [currentPlan?.destination]);
  
  if (!currentPlan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-teal-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1.5rem' }}>
            <MapPin size={40} className="text-teal-600" />
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-3">No Plan Yet</h2>
          <p className="text-gray-500 mb-8">Start planning your next adventure!</p>
          <Link 
            to="/planning"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
            style={{ borderRadius: '9999px' }}
            data-testid="create-plan-btn"
          >
            <Zap size={20} />
            Create a Plan
          </Link>
        </div>
      </div>
    );
  }
  
  const formatCurrency = (amount) => contextFormatCurrency(amount || 0);
  
  const handleSave = async () => {
    try {
      await api.savePlan(currentPlan.id);
      addSavedPlan(currentPlan);
      setSaved(true);
      toast.success('Plan saved! 💾');
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };
  
  const handleFeedback = async (type) => {
    try {
      await api.submitPlanFeedback(currentPlan.id, user?.user_id || 'anonymous', type);
      setFeedbackGiven(type);
      toast.success('Thanks for your feedback! 🙏');
    } catch (error) {
      console.error('Feedback error:', error);
    }
  };
  
  const toggleDay = (day) => {
    setExpandedDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };
  
  const budget = currentPlan.budget_breakdown || {};
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 transition-all"
            style={{ borderRadius: '0.75rem' }}
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-teal-500 animate-pulse" style={{ borderRadius: '50%' }} />
              <h1 className="font-bold text-gray-900">{currentPlan.destination}</h1>
            </div>
            <p className="text-xs text-gray-500">{currentPlan.duration_days} days • AI Curated</p>
          </div>
          
          <button 
            onClick={handleSave}
            className={`p-2 transition-all ${saved ? 'text-teal-500 bg-teal-50 scale-110' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`}
            style={{ borderRadius: '0.75rem' }}
            data-testid="save-btn"
          >
            <Bookmark size={20} fill={saved ? 'currentColor' : 'none'} />
          </button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Hero Card - Enhanced */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden"
          style={{ borderRadius: '2rem' }}
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500 via-cyan-500 to-teal-600" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-3xl" style={{ borderRadius: '50%' }} />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 blur-2xl" style={{ borderRadius: '50%' }} />
          
          <div className="relative z-10 p-8 md:p-10">
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur text-white/90 text-sm font-medium mb-4"
              style={{ borderRadius: '9999px' }}
            >
              <Sparkles size={14} />
              AI-Powered Travel Plan
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-black text-white mb-2">
              {currentPlan.destination}
            </h2>
            <p className="text-white/80 text-lg mb-8">Your personalized adventure awaits</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white/10 backdrop-blur" style={{ borderRadius: '1rem' }}>
                <Calendar size={24} className="text-white/70 mb-2" />
                <p className="text-white font-bold text-xl">{currentPlan.duration_days} Days</p>
                <p className="text-white/60 text-sm">Duration</p>
              </div>
              <div className="p-4 bg-white/10 backdrop-blur" style={{ borderRadius: '1rem' }}>
                <Wallet size={24} className="text-white/70 mb-2" />
                <p className="text-white font-bold text-xl">{formatCurrency(budget.total)}</p>
                <p className="text-white/60 text-sm">Estimated</p>
              </div>
              <div className="p-4 bg-white/10 backdrop-blur" style={{ borderRadius: '1rem' }}>
                <Navigation size={24} className="text-white/70 mb-2" />
                <p className="text-white font-bold text-xl truncate">{currentPlan.origin || 'N/A'}</p>
                <p className="text-white/60 text-sm">From</p>
              </div>
              <div className="p-4 bg-white/10 backdrop-blur" style={{ borderRadius: '1rem' }}>
                <Star size={24} className="text-white/70 mb-2" />
                <p className="text-white font-bold text-xl">{(currentPlan.itinerary || []).reduce((acc, day) => acc + (day.activities?.length || 0), 0)}</p>
                <p className="text-white/60 text-sm">Activities</p>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Quick Actions - Redesigned */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Plane, label: 'Explore Transport', color: 'teal', to: `/transport?origin=${encodeURIComponent(currentPlan.origin || '')}&destination=${encodeURIComponent(currentPlan.destination)}`, external: true },
            { icon: Hotel, label: 'Find Hotels', color: 'rose', to: `/hotels?destination=${encodeURIComponent(currentPlan.destination)}&nights=${currentPlan.duration_days}`, external: true },
            { icon: Image, label: 'Share Trip', color: 'emerald', onClick: () => setShowTimeline(true) },
            { icon: Lightbulb, label: 'AI Insights', color: 'amber', onClick: () => setShowExplanation(!showExplanation) },
          ].map((action) => {
            const content = (
              <motion.div 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="h-full"
              >
                <div className={`w-12 h-12 flex items-center justify-center mb-2 ${
                  action.color === 'teal' ? 'bg-gradient-to-br from-teal-100 to-cyan-100' :
                  action.color === 'rose' ? 'bg-gradient-to-br from-rose-100 to-pink-100' :
                  action.color === 'emerald' ? 'bg-gradient-to-br from-emerald-100 to-green-100' : 'bg-gradient-to-br from-amber-100 to-orange-100'
                }`} style={{ borderRadius: '1rem' }}>
                  <action.icon size={22} className={
                    action.color === 'teal' ? 'text-teal-600' :
                    action.color === 'rose' ? 'text-rose-600' :
                    action.color === 'emerald' ? 'text-emerald-600' : 'text-amber-600'
                  } />
                </div>
                <span className="text-gray-700 text-sm font-semibold">{action.label}</span>
                {action.external && <ExternalLink size={12} className="absolute top-3 right-3 text-gray-300" />}
              </motion.div>
            );
            
            if (action.to) {
              return (
                <Link
                  key={action.label}
                  to={action.to}
                  className="relative p-4 bg-white/80 backdrop-blur-xl border border-gray-100 flex flex-col items-center gap-2 text-center hover:shadow-lg hover:border-teal-200 transition-all"
                  style={{ borderRadius: '1.25rem' }}
                  data-testid={`action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  {content}
                </Link>
              );
            }
            
            return (
              <button
                key={action.label}
                onClick={action.onClick}
                className="relative p-4 bg-white/80 backdrop-blur-xl border border-gray-100 flex flex-col items-center gap-2 text-center hover:shadow-lg hover:border-teal-200 transition-all"
                style={{ borderRadius: '1.25rem' }}
                data-testid={`action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                {content}
              </button>
            );
          })}
        </div>
        
        {/* Events at Destination */}
        {destinationEvents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.5rem' }}>
                <TrendingUp size={16} className="text-amber-600" />
              </div>
              <span className="font-bold text-gray-800">Events at {currentPlan.destination}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {destinationEvents.map((event, index) => (
                <div 
                  key={event.id || index}
                  className="p-3 bg-white/80 backdrop-blur border border-amber-100/50"
                  style={{ borderRadius: '0.75rem' }}
                >
                  <p className="font-semibold text-gray-900 text-sm truncate">{event.name}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <Calendar size={10} />
                    {event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming'}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-600 mt-3 text-center">
              Time your trip around these events!
            </p>
          </motion.div>
        )}
        
        {/* AI Explanation */}
        <AnimatePresence>
          {showExplanation && currentPlan.explanation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-5 bg-white/80 backdrop-blur border border-gray-100 border-l-4 border-l-teal-400"
              style={{ borderRadius: '1rem' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={18} className="text-teal-500" />
                <span className="font-semibold text-gray-900">Why Panchi chose this</span>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {typeof currentPlan.explanation === 'string' 
                  ? currentPlan.explanation 
                  : currentPlan.explanation?.why_destination || 'This plan was crafted based on your preferences and travel style.'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Budget Breakdown */}
        <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow-lg" style={{ borderRadius: '1.5rem' }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
              <Wallet size={18} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Budget Breakdown</h3>
          </div>
          
          <div className="space-y-4">
            {[
              { icon: Hotel, label: 'Accommodation', amount: budget.accommodation, color: 'rose' },
              { label: 'Food & Dining', amount: budget.food, color: 'orange', emoji: '🍽️' },
              { icon: Plane, label: 'Transportation', amount: budget.transportation || budget.transport, color: 'teal' },
              { label: 'Activities', amount: budget.activities, color: 'purple', emoji: '🎫' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-4">
                <div className={`w-10 h-10 flex items-center justify-center ${
                  item.color === 'rose' ? 'bg-rose-100' :
                  item.color === 'orange' ? 'bg-orange-100' :
                  item.color === 'teal' ? 'bg-teal-100' : 'bg-purple-100'
                }`} style={{ borderRadius: '0.75rem' }}>
                  {item.icon ? (
                    <item.icon size={18} className={
                      item.color === 'rose' ? 'text-rose-600' :
                      item.color === 'orange' ? 'text-orange-600' :
                      item.color === 'teal' ? 'text-teal-600' : 'text-purple-600'
                    } />
                  ) : (
                    <span className="text-lg">{item.emoji}</span>
                  )}
                </div>
                <span className="flex-1 text-gray-600">{item.label}</span>
                <span className="font-semibold text-gray-900">{formatCurrency(item.amount)}</span>
              </div>
            ))}
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <span className="font-bold text-gray-900">Total Estimated</span>
              <span className="text-2xl font-black bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                {formatCurrency(budget.total)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Day-by-Day Itinerary */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
              <Calendar size={18} className="text-teal-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Day-by-Day Itinerary</h3>
            <span className="ml-auto px-3 py-1 bg-teal-50 border border-teal-100 text-teal-600 text-xs font-semibold flex items-center gap-1" style={{ borderRadius: '9999px' }}>
              <Sparkles size={12} />
              AI Curated
            </span>
          </div>
          
          <div className="space-y-3">
            {(currentPlan.itinerary || []).map((day, index) => (
              <DayCard 
                key={day.day} 
                day={day} 
                expanded={expandedDays.includes(day.day)}
                onToggle={() => toggleDay(day.day)}
                isFirst={index === 0}
                formatCurrency={formatCurrency}
              />
            ))}
          </div>
        </div>
        
        {/* Feedback Section */}
        <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow text-center" style={{ borderRadius: '1.5rem' }}>
          <h4 className="font-semibold text-gray-900 mb-4">Was this plan helpful?</h4>
          
          {feedbackGiven ? (
            <div className="flex items-center justify-center gap-2 text-teal-600">
              <Star size={18} fill="currentColor" />
              <span>Thanks for your feedback!</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => handleFeedback('up')}
                className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-emerald-50 hover:text-emerald-600 transition-colors text-gray-600 font-medium"
                style={{ borderRadius: '9999px' }}
                data-testid="feedback-up"
              >
                <ThumbsUp size={18} />
                <span>Yes!</span>
              </button>
              <button
                onClick={() => handleFeedback('down')}
                className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-red-50 hover:text-red-600 transition-colors text-gray-600 font-medium"
                style={{ borderRadius: '9999px' }}
                data-testid="feedback-down"
              >
                <ThumbsDown size={18} />
                <span>Not really</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Support CTA */}
        <Link 
          to="/support"
          className="flex items-center gap-4 p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100"
          style={{ borderRadius: '1.5rem' }}
          data-testid="support-cta"
        >
          <div className="w-12 h-12 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
            <Heart size={24} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">Enjoying Panchi? 💛</p>
            <p className="text-sm text-gray-500">Support our Open Beta - 100% optional</p>
          </div>
        </Link>
        
        {/* New Plan Button */}
        <div className="text-center pb-8">
          <Link 
            to="/planning"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold hover:border-teal-300 hover:text-teal-600 transition-all"
            style={{ borderRadius: '9999px' }}
            data-testid="new-plan-btn"
          >
            <Zap size={18} />
            Plan Another Trip
          </Link>
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      {/* Timeline Generator Modal */}
      <TimelineGenerator 
        plan={currentPlan} 
        isOpen={showTimeline} 
        onClose={() => setShowTimeline(false)} 
      />
      
      <FloatingSupport />
    </div>
  );
}

function DayCard({ day, expanded, onToggle, isFirst, formatCurrency }) {
  const getActivityIcon = (time, type) => {
    // First try to match activity type
    const typeIcons = {
      'sightseeing': Camera,
      'food': Utensils,
      'restaurant': Utensils,
      'dining': Utensils,
      'breakfast': Coffee,
      'morning': Sunrise,
      'evening': Sunset,
      'night': Moon,
      'entertainment': Music,
    };
    
    const lowerType = (type || '').toLowerCase();
    for (const [key, IconComponent] of Object.entries(typeIcons)) {
      if (lowerType.includes(key)) {
        return IconComponent;
      }
    }
    
    // Fallback to time-based icons
    const hour = parseInt(time?.split(':')[0]) || 12;
    if (hour >= 6 && hour < 10) return Sunrise;
    if (hour >= 10 && hour < 14) return Camera;
    if (hour >= 14 && hour < 18) return Coffee;
    if (hour >= 18 && hour < 21) return Utensils;
    return Moon;
  };
  
  const getTimeColor = (time) => {
    const hour = parseInt(time?.split(':')[0]) || 12;
    if (hour >= 6 && hour < 10) return 'from-amber-400 to-orange-400';
    if (hour >= 10 && hour < 14) return 'from-teal-400 to-cyan-400';
    if (hour >= 14 && hour < 18) return 'from-purple-400 to-pink-400';
    if (hour >= 18 && hour < 21) return 'from-rose-400 to-red-400';
    return 'from-indigo-400 to-purple-400';
  };
  
  return (
    <motion.div 
      className="bg-white/90 backdrop-blur-xl border border-gray-100 shadow-lg overflow-hidden"
      style={{ borderRadius: '1.25rem' }}
      layout
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50/50 transition-colors"
        data-testid={`day-${day.day}-toggle`}
      >
        <div className={`w-14 h-14 flex items-center justify-center font-black text-xl text-white shadow-lg ${
          isFirst 
            ? 'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/30' 
            : 'bg-gradient-to-br from-teal-500 to-cyan-500 shadow-teal-500/30'
        }`} style={{ borderRadius: '1rem' }}>
          {day.day}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-bold text-gray-900 text-lg">{day.title}</h4>
            {isFirst && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-600 text-xs font-semibold" style={{ borderRadius: '9999px' }}>
                Arrival
              </span>
            )}
          </div>
          {day.daily_summary && (
            <p className="text-sm text-gray-500 line-clamp-1">{day.daily_summary}</p>
          )}
          <p className="text-xs text-teal-600 mt-1">
            {(day.activities || []).length} activities planned
          </p>
        </div>
        <motion.div 
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-8 h-8 flex items-center justify-center bg-gray-100"
          style={{ borderRadius: '50%' }}
        >
          <ChevronDown size={18} className="text-gray-500" />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {expanded && day.activities && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-5 pb-5"
          >
            <div className="space-y-4 pt-2 border-t border-gray-100">
              {day.activities.map((activity, idx) => {
                const IconComponent = getActivityIcon(activity.time, activity.activity);
                return (
                  <motion.div 
                    key={idx} 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex gap-4"
                  >
                    {/* Timeline */}
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 bg-gradient-to-br ${getTimeColor(activity.time)} flex items-center justify-center text-white shadow-md z-10`} style={{ borderRadius: '0.75rem' }}>
                        <IconComponent size={18} />
                      </div>
                      {idx < day.activities.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gradient-to-b from-gray-200 to-transparent my-2" />
                      )}
                    </div>
                    
                    {/* Activity Card */}
                    <div className="flex-1 p-4 bg-gradient-to-br from-gray-50 to-white border border-gray-100" style={{ borderRadius: '1rem' }}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                            <Clock size={12} />
                            <span className="font-medium">{activity.time}</span>
                          </div>
                          <h5 className="font-semibold text-gray-900">{activity.activity}</h5>
                        </div>
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-sm font-bold flex-shrink-0" style={{ borderRadius: '9999px' }}>
                          {formatCurrency(activity.estimated_cost || 0)}
                        </span>
                      </div>
                      {activity.description && (
                        <p className="text-sm text-gray-500 mt-2">{activity.description}</p>
                      )}
                      {activity.location && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                          <MapPin size={10} />
                          <span>{activity.location}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
